import 'react-datepicker/dist/react-datepicker.css';
import React from 'react';
import useFetch, { CachePolicies } from 'use-http';
import { useParams } from 'react-router-dom';
import Paginate from 'components/UI/Paginate';
import Table from 'components/UI/Table';
import usePaginate from 'hooks/usePaginate';
import moment from 'moment-timezone';
import { ISession } from 'types/session';
import { convertUserFilter, formatUserFullName } from 'utils/helpers';
import AsyncSelect from 'components/UI/AsyncSelect';
import Button from 'components/UI/Button';
import ZoomComponent from 'components/UI/Zoom';
import Select from 'components/UI/Select';
import { IStores } from 'types/stores';
import { getSortImage } from './utils';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import { $storeStr } from 'models/store';
import { GroupsEnum } from 'types/users';
import { POSITION_MAPPING, PER_PAGE } from './common/constants';
import { createSortHandler, buildQueryParams } from './common/tableUtils';
import { getFileUrl } from 'utils/fileUrl';
import { useFilterInitialization } from './common/hooks/useFilterInitialization';
import { useFilterPagination } from './common/hooks/useFilterPagination';
import {
	createDateFilterFunction,
	createSessionManagerFormatter,
	createManagerFilterFunction,
} from './common/formatters';
import { createSessionsExcelExport } from './common/excelExportUtils';
import { formatServiceTypeTitle } from 'utils/helpers';

// Форматтер для дат Sessions с учетом таймзоны (специфичный для ISession)
const createSessionDateFormatter = (field: 'begin' | 'end', useMoscowTime = false) =>
	React.useCallback((row: ISession) => {
		const data = row[field];
		if (!data) return <div />;

		if (useMoscowTime) {
			return <div>{moment.tz(data.createdAt, 'Europe/Moscow').format('DD.MM.YY HH:mm')}</div>;
		}

		return (
			<div>
				{moment
					.utc(data.createdAt)
					.add(-1 * (data?.timezoneOffset ?? 0), 'minutes')
					.format('DD.MM.YY HH:mm')}
			</div>
		);
	}, []);

import { useSessionsData } from './common/hooks/useSessionsData';

const typesEngToRs = {
	manual: 'ручная',
	auto: 'авто',
	outsource: 'ИСИТ',
};

export default function Sessions() {
	const { storeId } = useParams();
	const { del, get, loading, error } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });
	const user = useUnit($user);
	const workList = useUnit($storeStr);
	const [data, setData] = React.useState([]);

	const { filter, setFilter } = useFilterInitialization(user);
	const { pageCount, offset, setCount, handlePageClick, resetOffset, currentPage } = usePaginate(PER_PAGE);

	const { userList, isLoadingUsers, storesList, registrationTypeOptions, regionalManagersOptions, aoManagersOptions, loadUser } =
		useSessionsData(storeId, filter);

	const currentStore = React.useMemo(() => {
		return storesList?.find((store: IStores) => store.id === storeId) || null;
	}, [storesList, storeId]) as IStores | null;

	const load = async () => {
		const params = buildQueryParams(filter);
		const result = await get(`/api/session?offset=${offset}&limit=${PER_PAGE}&storeId=${storeId}&${params}`);

		if (result?.rows) {
			setData(result.rows.map((row: any) => ({ ...row, uri: row?.preview?.uri ? row.preview.uri : null })));
			setCount(result.count);
		} else {
			setData([]);
			setCount(0);
		}
	};

	useFilterPagination(filter, resetOffset);

	React.useEffect(() => {
		load();
	}, [offset, PER_PAGE, filter, storeId]);

	const sortTable = createSortHandler(filter, setFilter);

	const headers = [
		{
			value: 'position',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Тип услуги</span>
					<button onClick={() => sortTable('user.position')}>
						{getSortImage('user.position', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			formatted: React.useCallback((row: any) => {
				const position = row.user?.position ?? row.user?.outsourcing?.pos?.title;
				const shortPosition = POSITION_MAPPING[position] || formatServiceTypeTitle(position);
				return <div>{shortPosition}</div>;
			}, []),
		},
		{
			value: 'name',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>ФИО</span>
					<button onClick={() => sortTable('user.secondname')}>
						{getSortImage('user.secondname', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			formatted: React.useCallback((row: any) => {
				return <div>{formatUserFullName(row.user)}</div>;
			}, []),
		filter: React.useCallback(() => {
			return (
				<AsyncSelect
					width="200px"
					onChange={(newValue: any) => {
						setFilter({ ...filter, userId: newValue ?? null });
					}}
					load={loadUser}
					isClearable
					defaultOptions={isLoadingUsers ? [] : convertUserFilter(userList)}
					debounceTimeout={1000}
				/>
			);
		}, [filter, setFilter, userList, loadUser, isLoadingUsers]),
		},
		{
			value: 'startAt',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Время <br /> прихода
					</span>

					<button onClick={() => sortTable('begin.createdAt')}>
						{getSortImage('begin.createdAt', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '100px',
			formatted: createSessionDateFormatter('begin', false),
			filter: createDateFilterFunction(filter, setFilter),
		},
		{
			value: 'leftAt',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Время
						<br /> ухода
					</span>
					<button onClick={() => sortTable('end.createdAt')}>
						{getSortImage('end.createdAt', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '100px',
			formatted: createSessionDateFormatter('end', false),
		},
		{
			value: 'startAtMscTime',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Время
						<br /> прихода
						<br /> МСК
					</span>
				</div>
			),
			width: '100px',
			formatted: createSessionDateFormatter('begin', true),
			filter: createSessionsExcelExport(filter, storeId),
		},
		{
			value: 'leftAtMscTime',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Время <br />
						уход
						<br /> МСК
					</span>
				</div>
			),
			width: '100px',
			formatted: createSessionDateFormatter('end', true),
		},
		{
			value: 'diff',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Сумма <br /> часов
					</span>
				</div>
			),
			formatted: React.useCallback((row: ISession) => {
				let diff = 0;
				if (row.begin && row.end) {
					const fromDate = moment(row.begin.createdAt);
					const toDate = moment(row.end.createdAt);
					diff = toDate.diff(fromDate, 'hours');
				}
				return <div>{diff}</div>;
			}, []),
		},
		{
			value: 'type',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Тип <br /> регистрации
					</span>
				</div>
			),
			formatted: React.useCallback((row: ISession) => {
				const typeSession = (row?.begin?.type ?? null) as keyof typeof typesEngToRs | null;
				return <div>{typeSession && typesEngToRs[typeSession]}</div>;
			}, []),
			filter: React.useCallback(() => {
				const handleChange = (newValue: any): void => {
					setFilter({ ...filter, type: newValue });
				};
				return <Select onChange={handleChange} options={registrationTypeOptions} isClearable width="100px" />;
			}, [filter, setFilter, registrationTypeOptions]),
		},
		{
			value: 'geo',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Геолокация <br /> (приход/уход)
					</span>
				</div>
			),
			formatted: React.useCallback((row: ISession) => {
				return (
					<div>
						{row?.begin?.lat && row?.begin?.lon && (
							<a
								href={`https://geotree.ru/coordinates?lat=${row?.begin?.lat}&lon=${row?.begin?.lon}&z=18&mlat=${row?.begin?.lat}&mlon=${row?.begin?.lon}`}
								target="_black"
							>
								Геотег 1
							</a>
						)}
						{row?.end?.lat && row?.end?.lon && (
							<a
								href={`https://geotree.ru/coordinates?lat=${row?.end?.lat}&lon=${row?.end?.lon}&z=18&mlat=${row?.end?.lat}&mlon=${row?.end?.lon}`}
								target="_black"
							>
								{' '}
								Геотег 2
							</a>
						)}
					</div>
				);
			}, []),
		},
		{
			value: 'photo',
			title: 'Фото',
			width: '200px',
			formatted: React.useCallback((row: ISession) => {
				if (!row.begin && !row.end) return null;
				return (
					<div>
						{row.begin?.file && <ZoomComponent src={getFileUrl(row.begin.file)} />}
						{row.end?.file && <ZoomComponent src={getFileUrl(row.end.file)} />}
					</div>
				);
			}, []),
		},
		{
			value: 'regional-director',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Закрепленный <br /> РД
					</span>
				</div>
			),
			width: '150px',
			formatted: createSessionManagerFormatter('regional_manager'),
			filter: createManagerFilterFunction(filter, setFilter, regionalManagersOptions, 'regional_manager', '100px'),
			visible:
				!workList.includes('Розница') &&
				(workList.includes('Региональный директор') || workList.includes('Заместитель регионального директора')),
		},
		{
			value: 'administrative-department',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>
						Закрепленный <br /> АО
					</span>
				</div>
			),
			width: '150px',
			formatted: createSessionManagerFormatter('ao_manager'),
			filter: createManagerFilterFunction(filter, setFilter, aoManagersOptions, 'ao_manager', '100px'),
			visible: !workList.includes('Розница') && workList.includes('Начальник отдела'),
		},
		{
			value: 'actions',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Действия</span>
				</div>
			),
			width: '100px',
			formatted: React.useCallback(
				(row: ISession) => {
					const hasJournalAdminAccess = user?.groups?.includes(GroupsEnum.JOURNAL_ADMIN);

					if (!hasJournalAdminAccess) return null;

					const handleDelete = async () => {
						if (window.confirm('Вы уверены, что хотите удалить эту запись?')) {
							try {
								await del(`/api/session/${row.id}`);
								load();
							} catch (error) {
								console.error('Ошибка при удалении:', error);
								alert('Ошибка при удалении записи');
							}
						}
					};

					return (
						<div>
							<Button onClick={handleDelete} size="sm" variant="orange">
								Удалить
							</Button>
						</div>
					);
				},
				[user?.groups, get, load],
			),
			visible: user?.groups?.includes(GroupsEnum.JOURNAL_ADMIN),
		},
	];

	return (
		<div>
			{error && 'Error!'}
			<div style={{ display: 'flex', width: '300px' }}>
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '5px' }}>
					<p style={{ fontFamily: 'Roboto', fontSize: '14px', fontWeight: 'bold' }}>
						Номер секции: {currentStore?.number || ''}
					</p>
					<p style={{ fontFamily: 'Roboto', fontSize: '14px', fontWeight: 'bold' }}>
						Название секции: {currentStore?.title || ''}
					</p>
				</div>
		</div>
		<Table headers={headers} rows={data} isLoading={loading} />
		{pageCount > 1 && <Paginate handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />}
		</div>
	);
}
